import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, getToken } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { PageSpinner } from "@/components/ui/spinner";
import { formatDateTime } from "@/lib/utils";
import { Phone, PhoneIncoming, PhoneOutgoing, Plus, X, Clock } from "lucide-react";

interface CallLog {
    id: string;
    customer_id: string;
    customer_name: string;
    site_id: string;
    job_id: string;
    direction: "inbound" | "outbound";
    caller_number: string;
    callee_number: string;
    duration_seconds: number;
    recording_url: string;
    notes: string;
    logged_by_name: string;
    source: string;
    created_at: string;
}

/**
 * Internal recording URLs (`/api/call-logs/:id/recording`, set by the Twilio
 * recording webhook) need the JWT in the query string — an <audio> tag can't
 * send an Authorization header, so the server honors `?token=` for exactly this
 * path. Externally-hosted URLs (manually entered in the log form) are used as-is.
 */
function recordingSrc(url: string): string {
    if (!url.startsWith("/api/")) return url;
    const token = getToken();
    if (!token) return url;
    return `${url}${url.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}`;
}

export function CallLogList({ customerId, jobId, siteId }: { customerId?: string; jobId?: string; siteId?: string }) {
    const qc = useQueryClient();
    const [showForm, setShowForm] = useState(false);

    const params = new URLSearchParams({ limit: "100" });
    if (customerId) params.set("customerId", customerId);
    if (jobId) params.set("jobId", jobId);
    if (siteId) params.set("siteId", siteId);

    const { data, isLoading } = useQuery({
        queryKey: ["call-logs", customerId, jobId, siteId],
        queryFn: () => api.get<{ callLogs: CallLog[]; total: number }>(`/api/call-logs?${params}`),
    });

    const logs = data?.callLogs ?? [];

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold flex items-center gap-1.5">
                    <Phone className="h-4 w-4" /> Call Log ({data?.total ?? 0})
                </h3>
                <Button size="sm" variant="outline" onClick={() => setShowForm(!showForm)}>
                    {showForm ? <X className="h-3.5 w-3.5 mr-1" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
                    {showForm ? "Cancel" : "Log Call"}
                </Button>
            </div>

            {showForm && (
                <CallLogForm
                    defaultCustomerId={customerId}
                    defaultJobId={jobId}
                    defaultSiteId={siteId}
                    onSuccess={() => {
                        setShowForm(false);
                        qc.invalidateQueries({ queryKey: ["call-logs"] });
                    }}
                />
            )}

            {isLoading ? (
                <PageSpinner />
            ) : logs.length === 0 ? (
                // On the standalone /call-logs page a bare "No calls recorded."
                // read as a broken screen (nav audit). Embedded uses (customer /
                // job / site tabs) keep the one-liner — context is obvious there.
                !customerId && !jobId && !siteId ? (
                    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed py-14 px-6 text-center">
                        <div className="rounded-full bg-secondary p-3 mb-4">
                            <Phone className="h-6 w-6 text-foreground/60" />
                        </div>
                        <h2 className="text-base font-semibold text-shell-foreground">No calls logged yet</h2>
                        <p className="text-sm text-foreground/70 mt-1.5 max-w-md">
                            Phone activity shows up here two ways: the office logs calls by hand with the button below,
                            or calls flow in automatically once a Twilio phone integration is connected.
                        </p>
                        <Button size="sm" variant="outline" className="mt-4" onClick={() => setShowForm(true)}>
                            <Plus className="h-3.5 w-3.5 mr-1" /> Log Call
                        </Button>
                    </div>
                ) : (
                    <p className="text-sm text-foreground/80">No calls recorded.</p>
                )
            ) : (
                <div className="space-y-2">
                    {logs.map((log) => (
                        <div key={log.id} className="flex items-start gap-3 rounded-lg border p-3 text-sm">
                            {log.direction === "inbound" ? (
                                <PhoneIncoming className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                            ) : (
                                <PhoneOutgoing className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Badge variant="outline" className="text-[10px]">
                                        {log.direction}
                                    </Badge>
                                    {log.caller_number && (
                                        <span className="font-mono text-xs">{log.caller_number}</span>
                                    )}
                                    {log.callee_number && (
                                        <span className="text-xs text-foreground/80">&rarr; {log.callee_number}</span>
                                    )}
                                    {log.duration_seconds != null && (
                                        <span className="flex items-center gap-0.5 text-xs text-foreground/80">
                                            <Clock className="h-3 w-3" /> {Math.floor(log.duration_seconds / 60)}:
                                            {String(log.duration_seconds % 60).padStart(2, "0")}
                                        </span>
                                    )}
                                </div>
                                {log.customer_name && (
                                    <p className="text-xs text-foreground/80 mt-0.5">{log.customer_name}</p>
                                )}
                                {log.notes && <p className="mt-1 text-xs">{log.notes}</p>}
                                {log.recording_url && (
                                    <audio
                                        controls
                                        src={recordingSrc(log.recording_url)}
                                        className="mt-1.5 h-8 w-full max-w-xs"
                                        preload="none"
                                    />
                                )}
                                <p className="text-[10px] text-foreground/80 mt-1">
                                    {formatDateTime(log.created_at)} &middot; {log.logged_by_name ?? log.source}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export function CallLogForm({
    defaultCustomerId,
    defaultJobId,
    defaultSiteId,
    onSuccess,
}: {
    defaultCustomerId?: string;
    defaultJobId?: string;
    defaultSiteId?: string;
    onSuccess: () => void;
}) {
    const [direction, setDirection] = useState<"inbound" | "outbound">("inbound");
    const [callerNumber, setCallerNumber] = useState("");
    const [calleeNumber, setCalleeNumber] = useState("");
    const [durationMin, setDurationMin] = useState("");
    const [notes, setNotes] = useState("");
    const [recordingUrl, setRecordingUrl] = useState("");

    const mutation = useMutation({
        mutationFn: (body: Record<string, unknown>) => api.post("/api/call-logs", body),
        onSuccess,
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const body: Record<string, unknown> = { direction };
        if (defaultCustomerId) body.customerId = defaultCustomerId;
        if (defaultJobId) body.jobId = defaultJobId;
        if (defaultSiteId) body.siteId = defaultSiteId;
        if (callerNumber) body.callerNumber = callerNumber;
        if (calleeNumber) body.calleeNumber = calleeNumber;
        if (durationMin) body.durationSeconds = Math.round(Number(durationMin) * 60);
        if (notes) body.notes = notes;
        if (recordingUrl) body.recordingUrl = recordingUrl;
        mutation.mutate(body);
    };

    return (
        <Card className="border-primary/30 bg-primary/5">
            <CardContent className="pt-4">
                <form onSubmit={handleSubmit} className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-medium">Direction</label>
                            <Select
                                value={direction}
                                onChange={(e) => setDirection(e.target.value as "inbound" | "outbound")}
                                className="mt-1"
                            >
                                <option value="inbound">Inbound</option>
                                <option value="outbound">Outbound</option>
                            </Select>
                        </div>
                        <div>
                            <label className="text-xs font-medium">Duration (min)</label>
                            <Input
                                type="number"
                                step="0.5"
                                min="0"
                                value={durationMin}
                                onChange={(e) => setDurationMin(e.target.value)}
                                className="mt-1"
                                placeholder="5"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-medium">Caller #</label>
                            <Input
                                value={callerNumber}
                                onChange={(e) => setCallerNumber(e.target.value)}
                                className="mt-1"
                                placeholder="(555) 555-1234"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium">Callee #</label>
                            <Input
                                value={calleeNumber}
                                onChange={(e) => setCalleeNumber(e.target.value)}
                                className="mt-1"
                                placeholder="(555) 555-5678"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-medium">Recording URL (optional)</label>
                        <Input
                            value={recordingUrl}
                            onChange={(e) => setRecordingUrl(e.target.value)}
                            className="mt-1"
                            placeholder="https://…"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-medium">Notes</label>
                        <Textarea
                            rows={2}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className="mt-1"
                            placeholder="Call notes…"
                        />
                    </div>
                    <Button type="submit" size="sm" disabled={mutation.isPending}>
                        {mutation.isPending ? "Saving…" : "Save Call Log"}
                    </Button>
                </form>
            </CardContent>
        </Card>
    );
}

export default function CallLogsPage() {
    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-black tracking-tight text-shell-foreground">Call Logs</h1>
                <p className="text-sm text-shell-foreground/80">All recorded calls across customers and jobs</p>
            </div>
            <CallLogList />
        </div>
    );
}
